import * as React from "react";
import type { Registrant } from "../api.js";
import { Icon } from "./Icon.js";

export type EventInfo = {
  id: string;
  name: string;
  startsAt: string | null;
  endsAt: string | null;
};

export function EventFilter({
  events,
  rows,
  selected,
  onChange,
}: {
  events: EventInfo[];
  rows: readonly Registrant[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [search, setSearch] = React.useState("");
  const [showPast, setShowPast] = React.useState(false);

  const counts = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const k = r.eventName;
      if (!k) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  const { upcoming, past } = React.useMemo(() => {
    const now = Date.now();
    const up: EventInfo[] = [];
    const pa: EventInfo[] = [];
    for (const e of events) {
      const ref = e.endsAt ?? e.startsAt;
      const t = ref ? Date.parse(ref) : NaN;
      if (!Number.isNaN(t) && t < now) pa.push(e);
      else up.push(e);
    }
    up.sort((a, b) => sortByStart(a, b, "asc"));
    pa.sort((a, b) => sortByStart(a, b, "desc"));
    return { upcoming: up, past: pa };
  }, [events]);

  const q = search.trim().toLowerCase();
  const matches = React.useCallback(
    (e: EventInfo) => !q || e.name.toLowerCase().includes(q),
    [q],
  );

  const upcomingShown = React.useMemo(() => upcoming.filter(matches), [upcoming, matches]);
  const pastShown = React.useMemo(() => past.filter(matches), [past, matches]);

  // When searching, surface past matches even if the toggle is off so users
  // don't lose findability for events they typed by name.
  const showPastGroup = q ? pastShown.length > 0 : showPast && past.length > 0;

  function toggle(name: string) {
    const next = selected.includes(name)
      ? selected.filter((x) => x !== name)
      : [...selected, name];
    onChange(next);
  }

  const noResults = q && upcomingShown.length === 0 && pastShown.length === 0;

  return (
    <div className="ef-inline">
      <div className="ef-search">
        <Icon name="search" size={13} />
        <input
          className="text"
          placeholder="Find an event…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ height: 32, paddingLeft: 30, fontSize: 13 }}
        />
      </div>

      <div className="ef-body">
        {noResults && (
          <div className="ef-empty">No events match "{search}".</div>
        )}

        {upcomingShown.length > 0 && (
          <div className="ef-group">
            {upcomingShown.map((e) => (
              <EventRow
                key={e.id}
                label={e.name}
                count={counts.get(e.name) ?? 0}
                checked={selected.includes(e.name)}
                onToggle={() => toggle(e.name)}
              />
            ))}
          </div>
        )}

        {showPastGroup && (
          <div className="ef-group">
            <div className="ef-grouph">Past</div>
            {pastShown.map((e) => (
              <EventRow
                key={e.id}
                label={e.name}
                count={counts.get(e.name) ?? 0}
                checked={selected.includes(e.name)}
                onToggle={() => toggle(e.name)}
                dim
              />
            ))}
          </div>
        )}
      </div>

      {!q && past.length > 0 && (
        <button
          className="ef-toggle"
          onClick={() => setShowPast((s) => !s)}
        >
          {showPast ? "Hide past events" : `Show past events (${past.length})`}
        </button>
      )}

      <style>{`
        .ef-inline { display: flex; flex-direction: column; }
        .ef-search { position: relative; padding: 4px 0 8px; }
        .ef-search > svg { position: absolute; left: 10px; top: 13px; color: var(--ink-400); }
        .ef-body { overflow: auto; max-height: 360px; padding: 0 0 4px; }
        .ef-empty { padding: 16px; font-size: 13px; color: var(--ink-500); text-align: center; }
        .ef-group { padding: 4px 0; }
        .ef-grouph {
          display: flex; align-items: center; gap: 6px;
          font-size: 10.5px; font-weight: 800; letter-spacing: 0.08em;
          text-transform: uppercase; color: var(--ink-400);
          padding: 4px 8px;
        }
        .ef-toggle {
          margin-top: 6px;
          padding: 7px 10px;
          width: 100%;
          font-size: 12px; font-weight: 600;
          color: var(--ink-600);
          background: transparent;
          border: 1px dashed var(--ink-200);
          border-radius: 6px;
          cursor: pointer;
          text-align: center;
        }
        .ef-toggle:hover { background: var(--ink-100); color: var(--ink-800); border-color: var(--ink-300); }
      `}</style>
    </div>
  );
}

function sortByStart(a: EventInfo, b: EventInfo, dir: "asc" | "desc"): number {
  const ta = a.startsAt ? Date.parse(a.startsAt) : NaN;
  const tb = b.startsAt ? Date.parse(b.startsAt) : NaN;
  const aNull = Number.isNaN(ta);
  const bNull = Number.isNaN(tb);
  if (aNull && bNull) return a.name.localeCompare(b.name);
  if (aNull) return 1;
  if (bNull) return -1;
  return dir === "asc" ? ta - tb : tb - ta;
}

function EventRow({
  label,
  count,
  checked,
  onToggle,
  dim,
}: {
  label: string;
  count: number;
  checked: boolean;
  onToggle: () => void;
  dim?: boolean;
}) {
  return (
    <label className="facet-row">
      <input type="checkbox" className="checkbox" checked={checked} onChange={onToggle} />
      <span className="lbl" style={dim ? { color: "var(--ink-500)" } : undefined}>{label}</span>
      <span className="num">{count}</span>
    </label>
  );
}
